import React, { useState, useEffect } from 'react';
import { PageFrame } from '../../components';
import { createTebexBasket, fetchStorePackages } from '../../services/api';
import './Store.css';

// Category mapping for Tebex packages
const CATEGORY_KEYWORDS = {
  ranks: ['rank', 'vip', 'mvp', 'hero', 'legend'],
  coins: ['coin', 'coins', 'currency'],
  keys: ['key', 'crate'],
  cosmetics: ['cosmetic', 'hat', 'trail', 'pet', 'particle']
};

// Icon mapping for different package types
const CATEGORY_ICONS = {
  ranks: ['⭐', '💫', '👑', '🔥', '💎'],
  coins: ['💰', '💵', '🪙', '💸'],
  keys: ['🔑', '🗝️', '✨'],
  cosmetics: ['🎩', '🌈', '🐾', '✨']
};

function Store() {
  const [username, setUsername] = useState('');
  const [inputName, setInputName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [showAddedModal, setShowAddedModal] = useState(false);
  const [addedItem, setAddedItem] = useState(null);

  // Dynamic package loading
  const [storeItems, setStoreItems] = useState([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState(null);
  const [categories, setCategories] = useState([{ id: 'all', label: 'All', description: null }]);
  const [tebexCategories, setTebexCategories] = useState({});

  // Load username from localStorage
  useEffect(() => {
    const savedUsername = localStorage.getItem('voxale_username');
    if (savedUsername) {
      setUsername(savedUsername);
      setIsLoggedIn(true);
    }
  }, []);

  // Fetch packages from Tebex API
  useEffect(() => {
    const loadPackages = async () => {
      setIsLoadingPackages(true);
      setPackagesError(null);

      try {
        const { data, error } = await fetchStorePackages();

        if (error || !data) {
          throw new Error(error || 'Failed to load store packages');
        }

        if (data.error) {
          throw new Error(data.error);
        }

        // Backend now returns: {"packages": [...], "categories": [...]}
        // Also handle legacy format: {"data": [...]}
        let packages = [];
        let categoriesData = [];

        // New format: {packages: [...], categories: [...]}
        if (Array.isArray(data.packages)) {
          packages = data.packages;
        } else if (Array.isArray(data.data)) {
          // Legacy format
          packages = data.data;
        } else if (data.data?.packages && Array.isArray(data.data.packages)) {
          packages = data.data.packages;
        }

        // Extract Tebex categories
        if (Array.isArray(data.categories)) {
          categoriesData = data.categories;
        } else if (data.data?.categories && Array.isArray(data.data.categories)) {
          categoriesData = data.data.categories;
        }

        // Build a map of category ID to category data (including descriptions)
        const categoryMap = {};
        categoriesData.forEach(cat => {
          categoryMap[cat.id] = {
            name: cat.name || '',
            description: cat.description || ''
          };
        });
        setTebexCategories(categoryMap);

        if (!packages || packages.length === 0) {
          setStoreItems([]);
          setIsLoadingPackages(false);
          return;
        }

        // Transform Tebex packages to our store format
        const transformedItems = packages.map((pkg, index) => {
          const category = determineCategory(pkg.name, pkg.description);
          const icon = getIconForPackage(category, index);

          // Tebex pricing logic:
          // - base_price: Current price (already discounted if sale is active)
          // - discount: Discount AMOUNT in currency (e.g., 0.998 for €0.998 off)
          // - total_price: Price with tax included

          const currentPrice = pkg.base_price || 0;
          const discountAmount = pkg.discount || 0;
          const hasDiscount = discountAmount > 0;

          // Calculate original price before discount
          // original = current + discount_amount
          const originalPrice = hasDiscount
            ? currentPrice + discountAmount
            : currentPrice;

          return {
            id: pkg.id,
            packageId: pkg.id,
            category: category,
            icon: icon,
            name: pkg.name,
            desc: truncateDescription(pkg.description, 60),
            fullDesc: pkg.description || 'No description available.',
            price: currentPrice,
            basePrice: originalPrice,
            totalPrice: pkg.total_price || 0,
            discount: discountAmount,
            hasDiscount: hasDiscount,
            currency: pkg.currency || 'USD',
            currencyIso: pkg.currency || 'USD',
            btnClass: getCategoryButtonClass(category),
            imageUrl: pkg.image || null,
            expirationDate: pkg.expiration_date || null
          };
        });

        setStoreItems(transformedItems);

        // Extract unique categories
        const categorySet = new Set(transformedItems.map(item => item.category));
        const categoryList = [
          { id: 'all', label: 'All', description: null },
          ...Array.from(categorySet).map(cat => {
            // Try to find matching Tebex category by name
            const tebexCat = Object.values(categoryMap).find(tc =>
              tc.name.toLowerCase() === cat.toLowerCase()
            );

            return {
              id: cat,
              label: cat.charAt(0).toUpperCase() + cat.slice(1),
              description: tebexCat?.description || null
            };
          })
        ];
        setCategories(categoryList);

      } catch (err) {
        console.error('Error loading packages:', err);
        setPackagesError(err.message);
      } finally {
        setIsLoadingPackages(false);
      }
    };

    loadPackages();
  }, []);

  // Helper: Determine category from package name/description
  const determineCategory = (name, description) => {
    const text = `${name} ${description}`.toLowerCase();

    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (keywords.some(keyword => text.includes(keyword))) {
        return category;
      }
    }

    return 'other';
  };

  // Helper: Get icon for package
  const getIconForPackage = (category, index) => {
    const icons = CATEGORY_ICONS[category] || ['📦'];
    return icons[index % icons.length];
  };

  // Helper: Strip HTML tags for truncated description
  const stripHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Helper: Truncate description
  const truncateDescription = (text, maxLength) => {
    if (!text) return 'No description available';
    const plainText = stripHtml(text);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  // Helper: Get button class for category
  const getCategoryButtonClass = (category) => {
    switch (category) {
      case 'ranks': return 'btn-gold';
      case 'coins': return 'btn-emerald';
      case 'keys': return 'btn-sapphire';
      case 'cosmetics': return 'btn-emerald';
      default: return 'btn-gold';
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (inputName.trim().length >= 3) {
      localStorage.setItem('voxale_username', inputName.trim());
      setUsername(inputName.trim());
      setIsLoggedIn(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('voxale_username');
    setUsername('');
    setIsLoggedIn(false);
    setCart([]);
    setCouponCode('');
  };

  const addToCart = (item) => {
    // Clear any existing checkout errors
    setCheckoutError('');

    // If adding a rank, remove any other ranks from cart
    let updatedCart = cart;
    if (item.category === 'ranks') {
      updatedCart = cart.filter(i => i.category !== 'ranks');
    }

    const existingItem = updatedCart.find(i => i.id === item.id);
    if (existingItem) {
      setCart(updatedCart.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setCart([...updatedCart, { ...item, quantity: 1 }]);
    }
    
    // Show confirmation modal
    setAddedItem(item);
    setShowAddedModal(true);
    setSelectedItem(null);
  };

  const removeFromCart = (itemId) => {
    // Clear any existing checkout errors
    setCheckoutError('');
    setCart(cart.filter(i => i.id !== itemId));
  };

  const updateQuantity = (itemId, delta) => {
    // Clear any existing checkout errors
    setCheckoutError('');
    setCart(cart.map(i => {
      if (i.id === itemId) {
        const newQty = i.quantity + delta;
        return newQty > 0 ? { ...i, quantity: newQty } : i;
      }
      return i;
    }).filter(i => i.quantity > 0));
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      setCheckoutError('Your cart is empty');
      return;
    }

    setIsCheckingOut(true);
    setCheckoutError('');

    try {
      // Prepare basket data for API
      const basketData = {
        username: username,
        items: cart.map(item => ({
          packageId: item.packageId,
          quantity: item.quantity
        })),
        couponCode: couponCode || null
      };

      // Create basket via backend
      const { data, error } = await createTebexBasket(basketData);

      if (error || !data) {
        throw new Error(error || 'Failed to create checkout');
      }

      if (data.error) {
        // Parse Tebex error messages for user-friendly display
        let errorMessage = data.error;

        // Check if error contains JSON with detail message
        try {
          const jsonMatch = data.error.match(/\{.*\}/);
          if (jsonMatch) {
            const errorJson = JSON.parse(jsonMatch[0]);
            if (errorJson.detail === "You own packages that are exclusive to this one") {
              errorMessage = "You are trying to buy a package you already own. Remove this package and try again.";
            } else if (errorJson.detail) {
              errorMessage = errorJson.detail;
            }
          }
        } catch (e) {
          // If parsing fails, use original error message
        }

        throw new Error(errorMessage);
      }

      if (!data.checkoutIdent) {
        throw new Error('No checkout identifier received');
      }

      // Launch Tebex checkout
      const checkoutUrl = `https://checkout.tebex.io/checkout/${data.checkoutIdent}`;
      
      // Detect if mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // On mobile, always use direct link (modals are unreliable)
        window.location.href = checkoutUrl;
      } else if (window.Tebex) {
        // Desktop: Try modal first with fallback
        try {
          let modalOpened = false;
          
          window.Tebex.checkout.init({
            ident: data.checkoutIdent,
            theme: 'dark',
            colors: [
              { name: 'primary', color: '#d4a017' },
              { name: 'secondary', color: '#3d2914' }
            ]
          });

          // Track if modal successfully opens
          const originalOpen = window.Tebex.checkout.launch;
          window.Tebex.checkout.launch = function() {
            modalOpened = true;
            return originalOpen.apply(this, arguments);
          };

          window.Tebex.checkout.launch();

          // Fallback: If modal didn't open within 1 second, open new tab
          setTimeout(() => {
            if (!modalOpened) {
              window.open(checkoutUrl, '_blank');
            }
          }, 1000);
        } catch (e) {
          // If anything fails, open in new tab
          console.error('Tebex modal error:', e);
          window.open(checkoutUrl, '_blank');
        }
      } else {
        // No Tebex library, use direct link
        window.open(checkoutUrl, '_blank');
      }

      // Clear cart after successful checkout launch
      setCart([]);
      setCouponCode('');
      setShowCart(false);

    } catch (err) {
      console.error('Checkout error:', err);
      setCheckoutError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const filteredItems = activeCategory === 'all'
    ? storeItems
    : storeItems.filter(item => item.category === activeCategory);

  // Currency formatter (always EUR)
  const formatPrice = (amount) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch {
      return `€${amount.toFixed(2)}`;
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <PageFrame>
        <header className="header">
          <div className="sword-divider">
            <span className="sword-line"></span>
            <span className="sword-icon">🛒</span>
            <span className="sword-line"></span>
          </div>
          <h1 className="page-title">Server Store</h1>
          <p className="page-subtitle">Enter your Minecraft username to continue</p>
        </header>

        <div className="login-box">
          <div className="login-icon">⛏️</div>
          <h2 className="login-title">Welcome to the Store</h2>
          <p className="login-desc">Please enter your Minecraft username to browse and purchase items. This ensures your purchases are delivered to the correct account.</p>
          
          <form onSubmit={handleLogin} className="login-form">
            <input
              type="text"
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              placeholder="Your Minecraft Username"
              className="login-input"
              minLength={3}
              maxLength={16}
              required
            />
            <button type="submit" className="btn btn-gold login-btn">
              Enter Store
            </button>
          </form>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      {/* User Header */}
      <div className="store-user-header">
        <div className="store-user-info">
          <img 
            src={`https://minotar.net/helm/${username}/32.png`} 
            alt={username} 
            className="store-user-avatar"
          />
          <span className="store-user-name">{username}</span>
        </div>
        <div className="store-user-actions">
          <button className="cart-btn" onClick={() => setShowCart(!showCart)}>
            🛒 Cart ({cart.reduce((sum, i) => sum + i.quantity, 0)})
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <header className="header" style={{ marginTop: '1rem' }}>
        <div className="sword-divider">
          <span className="sword-line"></span>
          <span className="sword-icon">🛒</span>
          <span className="sword-line"></span>
        </div>
        <h1 className="page-title">Server Store</h1>
        <p className="page-subtitle">Support the server and unlock exclusive perks</p>
      </header>

      {/* Cart Dropdown */}
      {showCart && (
        <div className="cart-dropdown">
          <h3 className="cart-title">Your Cart</h3>
          {cart.length === 0 ? (
            <p className="cart-empty">Your cart is empty</p>
          ) : (
            <>
              <div className="cart-items">
                {cart.map(item => (
                  <div className="cart-item" key={item.id}>
                    <div className="cart-item-info">
                      <span className="cart-item-icon">{item.icon}</span>
                      <span className="cart-item-name">{item.name}</span>
                    </div>
                    <div className="cart-item-controls">
                      {item.category !== 'ranks' && (
                        <>
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, -1)}>-</button>
                          <span className="cart-item-qty">{item.quantity}</span>
                          <button className="qty-btn" onClick={() => updateQuantity(item.id, 1)}>+</button>
                        </>
                      )}
                      <span className="cart-item-price">{formatPrice(item.price * item.quantity)}</span>
                      <button className="remove-btn" onClick={() => removeFromCart(item.id)}>×</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-coupon">
                <div className="coupon-input-row">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter coupon code (optional)"
                    className="coupon-input"
                  />
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--wood-medium)', marginTop: '0.5rem', textAlign: 'center' }}>
                  Coupon will be applied at checkout
                </p>
              </div>

              <div className="cart-totals">
                <div className="cart-total-row total">
                  <span>Subtotal:</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
              </div>

              {checkoutError && (
                <div style={{
                  background: '#ff4444',
                  color: 'white',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  {checkoutError}
                </div>
              )}

              <button
                className="btn btn-emerald checkout-btn"
                onClick={handleCheckout}
                disabled={isCheckingOut}
              >
                {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoadingPackages && (
        <div className="store-loading">
          <div className="loading-spinner"></div>
          <p>Loading store packages...</p>
        </div>
      )}

      {/* Error State */}
      {packagesError && !isLoadingPackages && (
        <div style={{
          background: 'rgba(255, 68, 68, 0.1)',
          border: '2px solid #ff4444',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          margin: '2rem 0'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <h3 style={{ color: '#ff4444', marginBottom: '0.5rem' }}>Failed to Load Store</h3>
          <p style={{ color: 'var(--wood-medium)' }}>{packagesError}</p>
          <button
            className="btn btn-gold"
            onClick={() => window.location.reload()}
            style={{ marginTop: '1rem' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingPackages && !packagesError && storeItems.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--wood-medium)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📦</div>
          <p>No packages available at this time.</p>
        </div>
      )}

      {/* Categories and Items (only show if loaded successfully) */}
      {!isLoadingPackages && !packagesError && storeItems.length > 0 && (
        <>
          <div className="store-categories">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`store-category ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {activeCategory === 'all' ? (
        // Show items grouped by category with dividers
        <>
          {categories.filter(cat => cat.id !== 'all').map((category, catIndex) => {
            const categoryItems = storeItems.filter(item => item.category === category.id);
            if (categoryItems.length === 0) return null;

            return (
              <React.Fragment key={category.id}>
                <div className="category-header" style={{ marginTop: catIndex === 0 ? '2rem' : '3rem' }}>
                  <h3 className="section-title">
                    <span className="title-line"></span>
                    {category.label}
                    <span className="title-line"></span>
                  </h3>
                  {category.description && (
                    <div
                      className="category-description"
                      dangerouslySetInnerHTML={{ __html: category.description }}
                    />
                  )}
                </div>
                <div className="store-grid">
                  {categoryItems.map((item) => (
                    <div className="store-item" key={item.id} onClick={() => setSelectedItem(item)}>
                      <div className="store-item-icon">{item.icon}</div>
                      <h3 className="store-item-name">{item.name}</h3>
                      <p className="store-item-desc">{item.desc}</p>
                      <div className="store-item-price">
                        {item.hasDiscount && (
                          <span style={{
                            textDecoration: 'line-through',
                            color: '#ff4444',
                            marginRight: '0.5rem',
                            fontSize: '0.9em'
                          }}>
                            {formatPrice(item.basePrice)}
                          </span>
                        )}
                        <span style={item.hasDiscount ? { color: '#22c55e', fontWeight: 'bold' } : {}}>
                          {formatPrice(item.price)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </React.Fragment>
            );
          })}
        </>
      ) : (
            // Show single category
            <>
              {activeCategory !== 'all' && categories.find(cat => cat.id === activeCategory)?.description && (
                <div
                  className="category-description"
                  style={{ textAlign: 'center', marginBottom: '1.5rem' }}
                  dangerouslySetInnerHTML={{ __html: categories.find(cat => cat.id === activeCategory)?.description }}
                />
              )}
              <div className="store-grid">
                {filteredItems.map((item) => (
                  <div className="store-item" key={item.id} onClick={() => setSelectedItem(item)}>
                    <div className="store-item-icon">{item.icon}</div>
                    <h3 className="store-item-name">{item.name}</h3>
                    <p className="store-item-desc">{item.desc}</p>
                    <div className="store-item-price">
                    {item.hasDiscount && (
                      <span style={{
                        textDecoration: 'line-through',
                        color: '#ff4444',
                        marginRight: '0.5rem',
                        fontSize: '0.9em'
                      }}>
                        {formatPrice(item.basePrice)}
                      </span>
                    )}
                    <span style={item.hasDiscount ? { color: '#22c55e', fontWeight: 'bold' } : {}}>
                      {formatPrice(item.price)}
                    </span>
                  </div>
                </div>
              ))}
              </div>
            </>
          )}
        </>
      )}

      <p style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--wood-medium)', fontSize: '0.9rem' }}>
        All purchases are processed securely through Tebex.<br />
        Purchases are non-refundable. By purchasing, you agree to our Terms of Service.
      </p>

      {/* Item Detail Popup */}
      {selectedItem && (
        <div className="popup-overlay" onClick={() => setSelectedItem(null)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setSelectedItem(null)}>×</button>
            <div className="popup-icon">{selectedItem.icon}</div>
            <h2 className="popup-title">{selectedItem.name}</h2>
            <div
              className="popup-desc"
              dangerouslySetInnerHTML={{ __html: selectedItem.fullDesc }}
            />
            <div className="popup-price">
              {selectedItem.hasDiscount && (
                <span style={{
                  textDecoration: 'line-through',
                  color: '#ff4444',
                  marginRight: '0.75rem',
                  fontSize: '0.85em'
                }}>
                  {formatPrice(selectedItem.basePrice)}
                </span>
              )}
              <span style={selectedItem.hasDiscount ? { color: '#22c55e', fontWeight: 'bold' } : {}}>
                {formatPrice(selectedItem.price)}
              </span>
            </div>
            <button
              className={`btn ${selectedItem.btnClass} popup-add-btn`}
              onClick={() => addToCart(selectedItem)}
            >
              Add to Cart
            </button>
          </div>
        </div>
      )}

      {/* Added to Cart Confirmation Modal */}
      {showAddedModal && addedItem && (
        <div className="popup-overlay" onClick={() => setShowAddedModal(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <button className="popup-close" onClick={() => setShowAddedModal(false)}>×</button>
            <div className="popup-icon" style={{ fontSize: '3rem' }}>✅</div>
            <h2 className="popup-title">Added to Cart!</h2>
            <p style={{ textAlign: 'center', color: 'var(--wood-medium)', marginBottom: '1.5rem' }}>
              <strong>{addedItem.name}</strong> has been added to your cart.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                className="btn btn-emerald"
                onClick={() => {
                  setShowAddedModal(false);
                  setShowCart(true);
                }}
                style={{ width: '100%' }}
              >
                Go to Cart
              </button>
              <button
                className="btn btn-gold"
                onClick={() => setShowAddedModal(false)}
                style={{ width: '100%' }}
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </PageFrame>
  );
}

export default Store;
